import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import toast from "../../../utils/toast";
import {
  getOwnerDashboardData,
  getAddCoinCollectionMode,
  getCollectionModeId,
  addOwnerFundRequest,
} from "../../../services/propertyOwner/propertyOwnerServices";

export default function PropOwnerDashboard() {
  const navigate = useNavigate();
  const [isDashboardData, setDashboardData] = useState(null);
  const [isOpenCoinModal, setIsOpenCoinModal] = useState(false);
  const [coinStep, setCoinStep] = useState("choose");
  const [coinAmount, setCoinAmount] = useState("");
  const [coinAmountErr, setCoinAmountErr] = useState("");
  const [collectionModes, setCollectionModes] = useState([]);
  const [selectedModeId, setSelectedModeId] = useState("");
  const [selectedInstrument, setSelectedInstrument] = useState(null);
  const [offlineForm, setOfflineForm] = useState({
    utr: "",
    paymentDate: "",
    screenshot: null,
    remark: "",
  });
  const [offlineErrors, setOfflineErrors] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const res = await getOwnerDashboardData();
      if (res.statusCode === 1) setDashboardData(res?.data);
    } catch (err) {
      console.log(err.response?.data?.message || "Failed to fetch dashboard");
    }
  };

  const fetchCollectionModes = async () => {
    try {
      const res = await getAddCoinCollectionMode();
      if (res.statusCode === 1) setCollectionModes(res.data ?? []);
    } catch (err) {
      console.log("Failed to fetch collection modes");
    }
  };

  const openCoinModal = () => {
    setCoinAmount("");
    setCoinAmountErr("");
    setCoinStep("choose");
    setSelectedModeId("");
    setSelectedInstrument(null);
    setOfflineForm({ utr: "", paymentDate: "", screenshot: null, remark: "" });
    setOfflineErrors({});
    setIsOpenCoinModal(true);
  };

  const closeCoinModal = () => {
    setIsOpenCoinModal(false);
    setCoinStep("choose");
    setCoinAmount("");
    setCoinAmountErr("");
    setSelectedModeId("");
    setSelectedInstrument(null);
    setOfflineForm({ utr: "", paymentDate: "", screenshot: null, remark: "" });
    setOfflineErrors({});
  };

  const handleSelectMethod = (method) => {
    if (!coinAmount || Number(coinAmount) < 100) {
      setCoinAmountErr(
        "⚠ Please enter a valid amount (minimum ₹100) before selecting a recharge method.",
      );
      return;
    }
    setCoinAmountErr("");
    if (method === "offline") {
      fetchCollectionModes();
      setCoinStep("offline");
    } else {
      setCoinStep("online");
    }
  };

  const handleModeChange = async (modeId) => {
    setSelectedModeId(modeId);
    if (!modeId) {
      setSelectedInstrument(null);
      return;
    }
    try {
      const res = await getCollectionModeId(modeId);
      if (res.statusCode === 1 && res.data?.length > 0) {
        const first = res.data.find((i) => i.isEnabled) ?? res.data[0];
        setSelectedInstrument(first);
      } else {
        setSelectedInstrument(null);
      }
    } catch (err) {
      console.log("Failed to fetch instruments");
      setSelectedInstrument(null);
    }
  };

  const handleOfflineSubmit = async () => {
    const e = {};
    if (!selectedModeId) e.modeId = "Please select a payment mode.";
    if (!offlineForm.utr) e.utr = "UTR / Reference Number is required.";
    if (!offlineForm.paymentDate) e.paymentDate = "Payment date is required.";
    if (selectedInstrument?.requireProof && !offlineForm.screenshot)
      e.screenshot = "Payment screenshot is required.";
    setOfflineErrors(e);
    if (Object.keys(e).length > 0) return;

    setSubmitLoading(true);
    try {
      const fd = new FormData();
      fd.append("Amount", coinAmount);
      fd.append("PaymentModeId", selectedModeId);
      fd.append("InstrumentName", selectedInstrument?.nickname || "");
      fd.append("UTR", offlineForm.utr);
      fd.append("Remark", offlineForm.remark);
      if (offlineForm.screenshot)
        fd.append("proofFile", offlineForm.screenshot);

      const res = await addOwnerFundRequest(fd);
      if (res.statusCode === 1) {
        toast.success(res.message);
        closeCoinModal();
        fetchDashboard();
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <>
      <div className="main">
        <div className="topbar">
          <div>
            <div className="page-title" id="page-title">
              Dashboard
            </div>
            <div className="page-sub" id="page-sub">
              Property overview
            </div>
          </div>
          <div className="topbar-right" id="topbar-right" />
        </div>
        <div className="content" id="content-area">
          <div
            style={{
              background: "linear-gradient(90deg,#0F6E56,#185FA5)",
              borderRadius: "var(--radius-md)",
              padding: "12px 20px",
              display: "flex",
              alignItems: "center",
              gap: 16,
              marginBottom: 18,
              flexWrap: "wrap",
            }}
          >
            <span style={{ fontSize: 22 }}>🪙</span>
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontSize: 10,
                  color: "rgba(255,255,255,0.7)",
                  textTransform: "uppercase",
                  letterSpacing: ".07em",
                }}
              >
                Coin Balance
              </div>
              <div style={{ fontSize: 22, fontWeight: 800, color: "#fff" }}>
                {isDashboardData?.walletBalance}
              </div>
            </div>
            <button
              onClick={openCoinModal}
              style={{
                background: "rgba(255,255,255,0.2)",
                border: "1px solid rgba(255,255,255,0.35)",
                color: "#fff",
                borderRadius: "var(--radius-md)",
                padding: "7px 16px",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              + Add Coins
            </button>
            <button
              onClick={() => navigate("/owner/fund-requests")}
              style={{
                background: "rgba(255,255,255,0.1)",
                border: "1px solid rgba(255,255,255,0.25)",
                color: "#fff",
                borderRadius: "var(--radius-md)",
                padding: "7px 14px",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              📥 {isDashboardData?.pendingFundRequestCount ?? 0} Requests
            </button>
          </div>

          <div className="stat-grid">
            <div className="stat-card">
              <div className="stat-icon">⊞</div>
              <div className="stat-label">Buildings</div>
              <div className="stat-value">
                {isDashboardData?.totalBuildings}
              </div>
              <div className="stat-delta delta-neu">Managed</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">⊟</div>
              <div className="stat-label">Total Rooms</div>
              <div className="stat-value">{isDashboardData?.totalRooms}</div>
              <div className="stat-delta delta-up">
                {isDashboardData?.occupancyPercent} occupied
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">◉</div>
              <div className="stat-label">Active Tenants</div>
              <div className="stat-value">
                {isDashboardData?.totalActiveTenants}
              </div>
              <div className="stat-delta delta-up">Across all buildings</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">💰</div>
              <div className="stat-label">
                Revenue ({isDashboardData?.revenueMonth})
              </div>
              <div className="stat-value">
                ₹ {isDashboardData?.revenueThisMonth}
              </div>
              <div className="stat-delta delta-up">
                {isDashboardData?.revenueGrowthPercent} vs{" "}
                {isDashboardData?.revenueLastMonthName}
              </div>
            </div>
            <div
              className="stat-card"
              style={{ cursor: "pointer", border: "1px solid #FCA5A5" }}
            >
              <div className="stat-icon">💸</div>
              <div className="stat-label">Total Due</div>
              <div className="stat-value" style={{ color: "#dc2626" }}>
                ₹ {isDashboardData?.totalDueAmount}
              </div>
              <div className="stat-delta delta-dn">
                {isDashboardData?.unpaidBillsCount} bill(s) unpaid · click to
                view
              </div>
            </div>
            <div
              onClick={() => navigate("/owner/fund-requests")}
              className="stat-card"
              style={{ cursor: "pointer", border: "1px solid #BFDBFE" }}
            >
              <div className="stat-icon">📥</div>
              <div className="stat-label">Fund Requests</div>
              <div className="stat-value" style={{ color: "#185FA5" }}>
                {isDashboardData?.pendingFundRequestCount}
              </div>
              <div className="stat-delta delta-dn">
                ₹ {isDashboardData?.pendingFundAmount} pending · click to view
              </div>
            </div>
            <div
              className="stat-card"
              style={{ cursor: "pointer", border: "1px solid #FCD34D" }}
            >
              <div className="stat-icon">⏳</div>
              <div className="stat-label">KYC Pending</div>
              <div className="stat-value" style={{ color: "#92400E" }}>
                {isDashboardData?.kycPendingCount}
              </div>
              <div className="stat-delta delta-dn">
                Awaiting approval · click to view
              </div>
            </div>
            <div
              className="stat-card"
              style={{ cursor: "pointer", border: "1px solid #e5e7eb" }}
            >
              <div className="stat-icon">○</div>
              <div className="stat-label">KYC Not Applied</div>
              <div className="stat-value" style={{ color: "#6B7280" }}>
                {isDashboardData?.kycNotAppliedCount}
              </div>
              <div className="stat-delta delta-dn">
                Yet to start · click to view
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-title">
              Current Plan:{" "}
              <span style={{ color: "var(--teal)" }}>
                {isDashboardData?.packageName}
              </span>
              <span className="badge badge-teal" style={{ marginLeft: 8 }}>
                {isDashboardData?.daysToExpiry} days left
              </span>
            </div>
            <p style={{ fontSize: 12, color: "var(--text-secondary)" }}>
              {isDashboardData?.packageTagline}
              <button className="btn btn-sm btn-teal" style={{ marginLeft: 8 }}>
                View Plans →
              </button>
            </p>
          </div>
        </div>
      </div>

      {/* ── Add Coin Modal ── */}
      {isOpenCoinModal && (
        <div className="modal-overlay open">
          <div className="modal" style={{ width: 480 }}>
            <div className="modal-header">
              <div className="modal-title">🪙 Add Coins</div>
              <button className="modal-close" onClick={closeCoinModal}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              {/* STEP 1 — Choose method */}
              {coinStep === "choose" && (
                <>
                  <div className="form-group" style={{ marginBottom: 16 }}>
                    <label>Amount (₹) *</label>
                    <input
                      type="number"
                      value={coinAmount}
                      onChange={(e) => {
                        setCoinAmount(e.target.value);
                        setCoinAmountErr("");
                      }}
                      placeholder="Enter amount e.g. 2499"
                      min={100}
                      style={{ fontSize: 15, padding: "10px 12px" }}
                    />
                    <div className="input-hint">1 Coin = ₹1 · Minimum ₹100</div>
                    {coinAmountErr && (
                      <div
                        style={{
                          color: "var(--red)",
                          fontSize: 11,
                          fontWeight: 600,
                          marginTop: 5,
                        }}
                      >
                        {coinAmountErr}
                      </div>
                    )}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: "var(--text-secondary)",
                      marginBottom: 10,
                    }}
                  >
                    How would you like to recharge?
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 12,
                    }}
                  >
                    <div
                      onClick={() => handleSelectMethod("online")}
                      style={{
                        border: "1.5px solid var(--border-mid)",
                        borderRadius: "var(--radius-md)",
                        padding: 20,
                        textAlign: "center",
                        cursor: "pointer",
                      }}
                    >
                      <div style={{ fontSize: 28, marginBottom: 8 }}>💳</div>
                      <div style={{ fontWeight: 700, fontSize: 13 }}>
                        Online
                      </div>
                      <div
                        style={{
                          fontSize: 11,
                          color: "var(--text-secondary)",
                          marginTop: 4,
                        }}
                      >
                        UPI, Card, NetBanking
                      </div>
                    </div>
                    <div
                      onClick={() => handleSelectMethod("offline")}
                      style={{
                        border: "1.5px solid var(--border-mid)",
                        borderRadius: "var(--radius-md)",
                        padding: 20,
                        textAlign: "center",
                        cursor: "pointer",
                      }}
                    >
                      <div style={{ fontSize: 28, marginBottom: 8 }}>🏦</div>
                      <div style={{ fontWeight: 700, fontSize: 13 }}>
                        Offline
                      </div>
                      <div
                        style={{
                          fontSize: 11,
                          color: "var(--text-secondary)",
                          marginTop: 4,
                        }}
                      >
                        Bank transfer, UPI, QR
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* STEP 2 — Offline */}
              {coinStep === "offline" && (
                <>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginBottom: 16,
                    }}
                  >
                    <button
                      className="btn btn-xs"
                      onClick={() => {
                        setCoinStep("choose");
                        setSelectedModeId("");
                        setSelectedInstrument(null);
                      }}
                    >
                      ← Back
                    </button>
                    <span
                      style={{ fontSize: 12, color: "var(--text-secondary)" }}
                    >
                      Amount : &nbsp;
                      <strong>
                        ₹ {Number(coinAmount).toLocaleString("en-IN")}
                      </strong>
                    </span>
                  </div>

                  <div className="form-group mb12">
                    <label>Payment Mode *</label>
                    <select
                      value={selectedModeId}
                      onChange={(e) => handleModeChange(e.target.value)}
                      style={
                        offlineErrors.modeId ? { borderColor: "#dc2626" } : {}
                      }
                    >
                      <option value="">— Select payment mode —</option>
                      {collectionModes.map((m) => (
                        <option key={m.modeId} value={m.modeId}>
                          {m.icon} {m.name}
                        </option>
                      ))}
                    </select>
                    {offlineErrors.modeId && (
                      <span style={{ color: "#dc2626", fontSize: 11 }}>
                        {offlineErrors.modeId}
                      </span>
                    )}
                  </div>

                  {/* Instrument details */}
                  {selectedInstrument &&
                    (() => {
                      const values = selectedInstrument.valuesJson
                        ? JSON.parse(selectedInstrument.valuesJson)
                        : {};
                      return (
                        <div
                          style={{
                            background: "var(--blue-light)",
                            borderRadius: "var(--radius-md)",
                            padding: "12px 14px",
                            marginBottom: 14,
                          }}
                        >
                          <div
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              color: "#0C447C",
                              marginBottom: 8,
                            }}
                          >
                            {selectedInstrument.modeIcon}{" "}
                            {selectedInstrument.modeName} —{" "}
                            {selectedInstrument.nickname}
                          </div>
                          {/* <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "130px 1fr",
                              gap: 3,
                              fontSize: 12,
                            }}
                          >
                            {Object.entries(values).map(([k, v]) => (
                              <>
                                <span
                                  style={{ color: "#0C447C", opacity: 0.75 }}
                                >
                                  {k}:
                                </span>
                                <span
                                  style={{
                                    fontWeight: 600,
                                    fontFamily: "monospace",
                                  }}
                                >
                                  {v}
                                </span>
                              </>
                            ))}
                          </div> */}
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "130px 1fr",
                              gap: 3,
                              fontSize: 12,
                            }}
                          >
                            {Object.entries(values).map(([k, v]) => (
                              <>
                                <span
                                  style={{ color: "#0C447C", opacity: 0.75 }}
                                >
                                  {k}:
                                </span>
                                {/* {console.log("qrCodeImageUrl",values.qrCodeImageUrl)} */}
                                {k === "qrCodeImageUrl" ? (
                                  <img
                                    src={v}
                                    alt="QR Code"
                                    style={{
                                      width: 80,
                                      height: 80,
                                      objectFit: "contain",
                                      borderRadius: 6,
                                      background: "#fff",
                                      padding: 4,
                                    }}
                                  />
                                ) : (
                                  <span
                                    style={{
                                      fontWeight: 600,
                                      fontFamily: "monospace",
                                    }}
                                  >
                                    {v}
                                  </span>
                                )}
                              </>
                            ))}
                          </div>
                        </div>
                      );
                    })()}

                  {/* Form fields — only show after mode selected */}
                  {selectedInstrument && (
                    <>
                      <div className="form-group mb12">
                        <label>UTR / Reference Number *</label>
                        <input
                          type="text"
                          value={offlineForm.utr}
                          onChange={(e) => {
                            setOfflineForm((p) => ({
                              ...p,
                              utr: e.target.value,
                            }));
                            setOfflineErrors((p) => ({ ...p, utr: "" }));
                          }}
                          placeholder="Enter UTR or transaction reference"
                          style={
                            offlineErrors.utr ? { borderColor: "#dc2626" } : {}
                          }
                        />
                        {offlineErrors.utr && (
                          <span style={{ color: "#dc2626", fontSize: 11 }}>
                            {offlineErrors.utr}
                          </span>
                        )}
                      </div>

                      <div className="form-group mb12">
                        <label>Payment Date *</label>
                        <input
                          type="date"
                          value={offlineForm.paymentDate}
                          onChange={(e) => {
                            setOfflineForm((p) => ({
                              ...p,
                              paymentDate: e.target.value,
                            }));
                            setOfflineErrors((p) => ({
                              ...p,
                              paymentDate: "",
                            }));
                          }}
                          style={
                            offlineErrors.paymentDate
                              ? { borderColor: "#dc2626" }
                              : {}
                          }
                        />
                        {offlineErrors.paymentDate && (
                          <span style={{ color: "#dc2626", fontSize: 11 }}>
                            {offlineErrors.paymentDate}
                          </span>
                        )}
                      </div>

                      <div className="form-group mb12">
                        <label>
                          Upload Payment Screenshot{" "}
                          {selectedInstrument.requireProof ? (
                            "*"
                          ) : (
                            <span style={{ fontWeight: 400, color: "#9ca3af" }}>
                              (Optional)
                            </span>
                          )}
                        </label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            setOfflineForm((p) => ({
                              ...p,
                              screenshot: e.target.files[0] || null,
                            }));
                            setOfflineErrors((p) => ({ ...p, screenshot: "" }));
                          }}
                          style={
                            offlineErrors.screenshot
                              ? { borderColor: "#dc2626" }
                              : {}
                          }
                        />
                        {selectedInstrument.requireProof && (
                          <div
                            className="input-hint"
                            style={{ color: "var(--red)" }}
                          >
                            Required — upload proof of payment for this
                            instrument
                          </div>
                        )}
                        {offlineErrors.screenshot && (
                          <span style={{ color: "#dc2626", fontSize: 11 }}>
                            {offlineErrors.screenshot}
                          </span>
                        )}
                      </div>

                      <div className="form-group mb14">
                        <label>Remark</label>
                        <input
                          type="text"
                          value={offlineForm.remark}
                          onChange={(e) =>
                            setOfflineForm((p) => ({
                              ...p,
                              remark: e.target.value,
                            }))
                          }
                          placeholder="e.g. Top-up for monthly credits"
                        />
                      </div>

                      <button
                        className="btn btn-primary "
                        style={{
                          width: "100%",
                          padding: 11,
                          fontSize: 14,
                          marginTop: "10px",
                        }}
                        onClick={handleOfflineSubmit}
                        disabled={submitLoading}
                      >
                        {submitLoading
                          ? "Submitting..."
                          : "Submit Recharge Request"}
                      </button>
                    </>
                  )}
                </>
              )}

              {/* STEP 2 — Online */}
              {coinStep === "online" && (
                <>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginBottom: 16,
                    }}
                  >
                    <button
                      className="btn btn-xs"
                      onClick={() => setCoinStep("choose")}
                    >
                      ← Back
                    </button>
                    <span
                      style={{ fontSize: 12, color: "var(--text-secondary)" }}
                    >
                      Amount:{" "}
                      <strong>
                        ₹{Number(coinAmount).toLocaleString("en-IN")}
                      </strong>
                    </span>
                  </div>
                  <div className="info-box">
                    You will be redirected to a secure payment gateway to
                    complete the transaction.
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
